import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const IntroductionPcPage = () => {
  const [animatedComponents, setAnimatedComponents] = useState({
    secure: false,
    verification: false,
    communication: false,
    shipping: false
  });

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const triggerPosition = windowHeight * 3 / 4; // 3/4 from top of viewport

      // Get positions of each component relative to viewport
      const secureEl = document.getElementById('secure-transaction');
      const verificationEl = document.getElementById('verification-section');
      const communicationEl = document.getElementById('communication-section');
      const shippingEl = document.getElementById('shipping-section');

      if (secureEl) {
        const rect = secureEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, secure: true }));
        }
      }
      if (verificationEl) {
        const rect = verificationEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, verification: true }));
        }
      }
      if (communicationEl) {
        const rect = communicationEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, communication: true }));
        }
      }
      if (shippingEl) {
        const rect = shippingEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, shipping: true }));
        }
      }
    };

    // Wait for page to be fully loaded before adding scroll listener and checking positions
    const initializeScrollHandler = () => {
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Check initial position after page load
    };

    if (document.readyState === 'complete') {
      initializeScrollHandler();
    } else {
      window.addEventListener('load', initializeScrollHandler);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('load', initializeScrollHandler);
    };
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#f4f4f4] overflow-x-hidden">
      <style jsx>{`
        img {
          user-drag: none;
          -webkit-user-drag: none;
        }
        .animated-orange-tag {
          clip-path: inset(0 99% 0 0);
          opacity: 0;
          transition: clip-path 500ms ease-out, opacity 500ms ease-out;
        }
        .animated-orange-tag.show {
          clip-path: inset(0 0 0 0);
          opacity: 1;
        }
      `}</style>
      
      <Header />
      
      {/* Hero Section */}
      <section className="w-full relative">
        <img
          src="/images/introduction/hero.jpg"
          alt="DONDONスタートガイド for ビギナーズ"
          className="w-full h-auto object-cover"
          draggable="false"
        />
      </section>

      {/* Steps Title Section */}
      <section className="w-full relative bg-[#f4f4f4] overflow-hidden">
        <img
          src="/images/introduction/intro_bg.png"
          alt="Background"
          className="w-full h-auto object-cover"
          draggable="false"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full px-16 max:px-20 pb-12 text-center">
            <h1 className="text-3xl max:text-4xl font-bold text-white mb-4" style={{textShadow: '0px 0px 10px rgba(32, 48, 88, 1.0)'}}>
              売りたい！買いたい！かんたん取引ステップ
            </h1>
            <p className="text-xl max:text-2xl font-bold text-white" style={{textShadow: '0px 0px 10px rgba(32, 48, 88, 1.0)'}}>
              DONDONの取引は簡単・安全・安心！
            </p>
          </div>
        </div>
        {/* Triangle arrow at bottom */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-[30px] border-l-transparent border-r-[30px] border-r-transparent border-t-[30px] border-t-[#f4f4f4]"></div>
      </section>

      {/* Trading Steps Section */}
      <section className="relative pb-12 px-0 w-[896px] mx-auto">
        {/* Connecting Lines - Under all views */}
        <div className="absolute inset-0 top-20 bottom-20 pointer-events-none z-0">
          <div className="w-full h-full relative">
            {/* Seller line - Left half */}
            <div className="absolute left-1/4 top-0 w-[9px] bg-[#3c5493] h-full transform -translate-x-1/2"></div>
            {/* Buyer line - Right half */}
            <div className="absolute right-1/4 top-0 w-[9px] bg-[#ff6a00] h-full transform translate-x-1/2"></div>
          </div>
        </div>
        
        {/* Seller and Buyer Icons */}
        <div className="flex relative z-10 mb-16">
          {/* Left half - Seller */}
          <div className="w-1/2 flex justify-center">
            <div className="flex flex-col items-center">
              <div className="bg-[#3c5493] text-white px-8 py-3 rounded-full mb-4">
                <span className="text-lg font-bold">売りたい人</span>
              </div>
              <img
                src="/images/introduction/seller.png"
                alt="売りたい人"
                className="w-40 h-40"
                draggable="false"
              />
            </div>
          </div>
          {/* Right half - Buyer */}
          <div className="w-1/2 flex justify-center">
            <div className="flex flex-col items-center">
              <div className="bg-[#ff6a00] text-white px-8 py-3 rounded-full mb-4">
                <span className="text-lg font-bold">買いたい人</span>
              </div>
              <img
                src="/images/introduction/buyer.png"
                alt="買いたい人"
                className="w-40 h-40"
                draggable="false"
              />
            </div>
          </div>
        </div>

        {/* Step 1: Listing and Purchase */}
        <div className="flex flex-row justify-between items-start gap-8 mb-12 relative z-10">
          <div className="w-[48%] bg-[#d2ddf8] rounded-xl border-r-4 border-b-4 border-[#3c5493] overflow-hidden">
            <div className="bg-[#d2ddf8] py-4 text-center">
              <h2 className="text-[#3c5493] text-2xl font-bold">商品を出品する</h2>
            </div>
            <div className="bg-white p-6 rounded-b-xl">
              <p className="text-lg text-gray-800">
                売りたい商品を選び、<span className="text-[#3c5493] font-bold">状態や価格を入力</span>します。<br/>
                項目を選ぶだけのカンタン出品！
              </p>
            </div>
          </div>
          <div className="w-[48%] bg-[#fbe0c9] rounded-xl border-r-4 border-b-4 border-[#ff6a00] overflow-hidden">
            <div className="bg-[#fbe0c9] py-4 text-center">
              <h2 className="text-[#ff6a00] text-2xl font-bold">最安値で購入する</h2>
            </div>
            <div className="bg-white p-6 rounded-b-xl">
              <p className="text-lg text-gray-800">
                欲しい商品を選ぶだけで、<span className="text-[#ff6a00] font-bold">自動的に最安値で購入！<br/></span>
                鑑定済の正規品が手に入ります。
              </p>
            </div>
          </div>
        </div>

        {/* Secure Transaction Section */}
        <div className="bg-white rounded-xl border-r-4 border-b-4 border-[#aaaaaa] p-8 mb-12 relative z-10">
          <div className="text-center mb-8">
            <div id="secure-transaction" className={`animated-orange-tag bg-[#ff6a00] text-white px-8 py-2 rounded inline-block ${animatedComponents.secure ? 'show' : ''}`}>
              <h2 className="text-2xl font-bold">DONDONが仲介するから安心安全！</h2>
            </div>
          </div>
          <div className="text-center mb-8">
            <img
              src="/images/introduction/steps.jpg"
              alt="取引の流れ"
              className="w-full max-w-4xl h-auto mx-auto"
              draggable="false"
            />
          </div>
          <p className="text-lg text-gray-800 max-w-4xl mx-auto">
            商品代金はDONDON運営本部で一時保管し、
            <span className="text-[#3c5493] font-bold">商品の鑑定後に出品者へ送金</span>されます。
            購入者の決済は<span className="text-[#ff6a00] font-bold">商品発送後に確定</span>となります。
          </p>
        </div>

        {/* Step 2: Shipping */}
        <div className="mb-12 relative z-10">
          <div className="w-[48%] bg-[#d2ddf8] rounded-xl border-r-4 border-b-4 border-[#3c5493] overflow-hidden">
            <div className="bg-[#d2ddf8] py-4 text-center">
              <h2 className="text-[#3c5493] text-2xl font-bold">商品を発送する</h2>
            </div>
            <div className="bg-white p-6 rounded-b-xl">
              <p className="text-lg text-gray-800">
                商品をDONDONへ発送。<br/>
                <span className="text-[#3c5493] font-bold">住所を購入者に知られることなく</span>完了！
              </p>
            </div>
          </div>
        </div>

        {/* Verification Section */}
        <div className="bg-white rounded-xl border-r-4 border-b-4 border-[#aaaaaa] p-8 mb-12 relative z-10">
          <div className="text-center mb-8">
            <img
              src="/images/introduction/icon.png"
              alt="DONDONアイコン"
              className="w-[134px] h-[99px] mx-auto mb-4 object-contain"
              draggable="false"
            />
          </div>
          <div className="flex flex-row gap-6 justify-center">
            {/* Main Verification Section - Half Width */}
            <div className="w-full bg-[#f4f4f4] rounded-xl border-r-4 border-b-4 p-6">
              <div className="text-center mb-6">
                <div id="verification-section" className={`animated-orange-tag bg-[#ff6a00] text-white px-6 py-2 rounded inline-block ${animatedComponents.verification ? 'show' : ''}`}>
                  <h3 className="text-xl font-bold">DONDONによる真贋鑑定</h3>
                </div>
              </div>
              <div className="flex gap-2 mb-6">
                <img
                  src="/images/introduction/verify1.jpg"
                  alt="鑑定作業"
                  className="w-[181px] h-[163px] object-cover rounded"
                  draggable="false"
                />
                <img
                  src="/images/introduction/verify2.jpg"
                  alt="鑑定シール"
                  className="w-[181px] h-[163px] object-cover rounded"
                  draggable="false"
                />
              </div>
              <p className="text-lg text-gray-800">
                取引される商品はすべてDONDONの<span className="text-[#ff6a00] font-bold">プロ鑑定士が真贋鑑定</span>を行います。
                正規品と認定された商品にはその証明として鑑定済シールを貼付します。
                万が一、正規品と鑑定された商品が偽造品だった場合は、商品の購入価格を上限なく<span className="text-[#ff6a00] font-bold">全額補償</span>します。
              </p>
            </div>
            {/* Side Sections */}
            <div className="w-full flex flex-col gap-4">
              <div className="bg-[#f4f4f4] rounded-xl border-r-4 border-b-4 p-6">
                <div className="text-center mb-4">
                  <div id="communication-section" className={`animated-orange-tag bg-[#ff6a00] text-white px-4 py-2 rounded inline-block ${animatedComponents.communication ? 'show' : ''}`}>
                    <h3 className="text-xl font-bold">取引のやり取り</h3>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <p className="text-lg text-gray-800 flex-1">
                    疑問や悩みなどは<span className="text-[#ff6a00] font-bold">チャットで<br/>質問</span>することができます。<br/>
                    DONDONスタッフがご対応<br/>させていただくため、<br/>お気軽にご利用ください。
                  </p>
                  <img
                    src="/images/introduction/chat.png"
                    alt="チャット"
                    className="w-16 h-full object-contain self-end"
                    draggable="false"
                  />
                </div>
              </div>
              <div className="bg-[#f4f4f4] rounded-xl border-r-4 border-b-4 p-6">
                <div className="text-center mb-4">
                  <div id="shipping-section" className={`animated-orange-tag bg-[#ff6a00] text-white px-4 py-2 rounded inline-block ${animatedComponents.shipping ? 'show' : ''}`}>
                    <h3 className="text-xl font-bold">DONDONから商品発送</h3>
                  </div>
                </div>
                <p className="text-lg text-gray-800">
                  商品を購入者へ発送します。<br/>発送はDONDONが行うため、<br/>
                  出品者・購入者ともに<span className="text-[#ff6a00] font-bold">住所を相手に知られることはありません！</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Final Step: Payment and Receipt */}
        <div className="flex flex-row justify-between items-start gap-8 relative z-10">
          <div className="w-[48%] bg-[#d2ddf8] rounded-xl border-r-4 border-b-4 border-[#3c5493] overflow-hidden">
            <div className="bg-[#d2ddf8] py-4 text-center">
              <h2 className="text-[#3c5493] text-2xl font-bold">振込申請</h2>
            </div>
            <div className="bg-white p-6 rounded-b-xl">
              <p className="text-lg text-gray-800">
                鑑定通過後、振込申請が可能となります。
              </p>
            </div>
          </div>
          <div className="w-[48%] bg-[#fbe0c9] rounded-xl border-r-4 border-b-4 border-[#ff6a00] overflow-hidden">
            <div className="bg-[#fbe0c9] py-4 text-center">
              <h2 className="text-[#ff6a00] text-2xl font-bold">商品を受け取る</h2>
            </div>
            <div className="bg-white p-6 rounded-b-xl">
              <p className="text-lg text-gray-800">
                商品を受け取れば取引はすべて完了です。
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default IntroductionPcPage;
